import { useState, type ChangeEvent, type ReactNode } from "react";
import { Link } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { dunningStageLabel, type DunningPolicy } from "@talentkontor/domain";
import { useStaffWorkspace, type StaffWorkspace } from "../../../app/session";
import { api } from "../../../data";
import { ApiError } from "../../../data/api";
import { useBrands, useDeals, useInvoices, useOrgSettings, usePeriods, useTalents } from "../../../data/queries";
import { describeError, ErrorState, Loading } from "../../../components/States";
import { Money, PillView, useAction } from "../../../components/ui";
import { date, money, monthLabel, today as todayIn } from "../../../lib/format";
import { useOrgConfig } from "../../../model/org-config";
import { downloadBlob } from "../einstellungen/parts";
import { loadLifecycle } from "../einstellungen/data";
import { useKooperationen } from "../kooperationen/hooks";
import {
  BESTAND_COLUMN_LABEL, KOOP_TEMPLATE, koopContext, koopRows, LEGACY_KIND_LABEL, RECHNUNG_TEMPLATE, rechnungContext, rechnungRows,
  type KoopImport, type RechnungImport,
} from "./altbestand-model";
import { importKooperation, importRechnung } from "./altbestand-data";
import { parseCsv, type BestandKind, type ImportRow } from "./import-model";
import { looksLikeXlsx, readXlsx } from "./xlsx";
import { pill } from "./model";
import { DemoNote } from "./parts";
import { EinzelRechnungErfassen } from "./EinzelRechnung";
import { talentFullName } from "./altbestand-model";
import "./stammdaten.css";
import { bookkeepingExportName } from "../einstellungen/model";
import { t, currentLang } from "../../../lib/sprache";
import { fehlerText } from "../../../lib/fehler";

/**
 * Bestand übernehmen (0064): laufende Kooperationen und offene Rechnungen aus dem bisherigen System, wie der Import von
 * Creatorn und Marken (Vorlage, Datei oder eingefügte Zellen, Vorschau mit Prüfung je Zeile, Dublettenhinweis). Angelegt wird
 * über die Wege der Datenbank: Kooperationen über deal_save (deal_import), Rechnungen über invoice_import_legacy. Es entstehen
 * keine Anforderungen, Mails oder Glocken.
 */

const MAX_ROWS = 500;

/**
 * Studio (Creator-Konto, Studio-Texte Runde 2/3): Vorlagen ohne Creator, Provision, Abrechnungsweg und Creator-Rechnungsarten.
 * Ohne Spalte Creator gehört jede Zeile zum eigenen Profil (koopRows/rechnungRows mit selfTalent).
 */
export const STUDIO_KOOP_VORLAGE = [
  "Marke;Titel;Modell;Stand;Währung;Honorar;Beginn;Monate;Leistungen;Plattformen;Nutzungsrechte bis;Content-Deadline;Stand vergangener Monate",
  "Lunava;Frühjahrskampagne;Einmalig;Abgeschlossen;EUR;2.500,00;03.2026;1;1 Reel und 3 Stories;Instagram;31.12.2026;20.03.2026;bezahlt",
  "Velune;Studio-Begleitung;Monatlich;In Produktion;EUR;1.200,00;06.2026;6;Je Monat ein Reel;Instagram, TikTok;;;abgerechnet",
].join("\r\n") + "\r\n";
export const STUDIO_RECHNUNG_VORLAGE = [
  "Nummer;Art;Marke;Deal;Leistungsmonat;Rechnungsdatum;Fälligkeit;Netto;Steuer;Brutto;Währung;Steuerfall;Bereits bezahlt;Mahnstufe;Letzte Mahnung;Bemerkung",
  "RE-2026-0412;an Marke;Lunava;Frühjahrskampagne;03.2026;31.03.2026;14.04.2026;2.500,00;475,00;2.975,00;EUR;Regelbesteuerung;1.000,00;1;02.05.2026;Rest per Überweisung zugesagt",
  "RE-2026-0415;an Marke;Velune;;04.2026;30.04.2026;14.05.2026;800,00;0,00;800,00;EUR;Kleinunternehmer;;;;",
].join("\r\n") + "\r\n";

/** Studio: Zeilen mit einer anderen Art als „an Marke“ werden nicht übernommen (es gibt keine Creator- und Provisionsrechnungen) */
export function nurAnMarke(rows: ImportRow<RechnungImport>[]): ImportRow<RechnungImport>[] {
  return rows.map((r) => (r.values && r.values.kind !== "agency_to_brand"
    ? { ...r, values: null, errors: [...r.errors, t("Im Creator-Konto gibt es nur Rechnungen an Marken (Art „an Marke“)", "A creator account only has invoices to brands (type “an Marke”)")] }
    : r));
}
type Result = { line: number; label: string; ok: boolean; text: string; to?: string };

const LABEL: Record<BestandKind, { title: string; one: string; many: string; back: string; backLabel: string; template: string; verb: string }> = {
  kooperationen: { get title() { return t("Kooperationen übernehmen", "Take over deals"); }, get one() { return t("Kooperation", "Deal"); }, get many() { return t("Kooperationen", "Deals"); }, back: "/agentur/kooperationen", get backLabel() { return t("Zurück zu den Kooperationen", "Back to deals"); },
    template: "kooperationen-vorlage.csv", get verb() { return t("übernommen", "imported"); } },
  rechnungen: { get title() { return t("Offene Rechnungen übernehmen", "Take over open invoices"); }, get one() { return t("Rechnung", "Invoice"); }, get many() { return t("Rechnungen", "Invoices"); }, back: "/agentur/rechnungen", get backLabel() { return t("Zurück zu den Rechnungen", "Back to invoices"); },
    template: "offene-rechnungen-vorlage.csv", get verb() { return t("übernommen", "imported"); } },
};

/** Meldung der Datenbank je Zeile (deutsch, mit Grund); Verbindungsfehler wie überall */
function rowError(e: unknown): string {
  if (e instanceof ApiError) return fehlerText(e);
  const d = describeError(e);
  return `${d.title} ${d.text}`.trim();
}

export function BestandImport({ kind }: { kind: BestandKind }) {
  const ws = useStaffWorkspace();
  return kind === "kooperationen" ? <KoopImportSeite ws={ws} /> : <RechnungImportSeite ws={ws} />;
}

/** Nur-Lesen-Phase der Agentur (gesperrt, gekündigt, Testphase abgelaufen) */
function useReadOnly(orgId: string) {
  return useQuery({ queryKey: ["org_lifecycle", orgId, "import"], queryFn: () => loadLifecycle(api, orgId).then((l) => !!l.read_only).catch(() => false) });
}

// ── Kooperationen ──────────────────────────────────────────────────────────────

function KoopImportSeite({ ws }: { ws: StaffWorkspace }) {
  const q = useKooperationen();
  const readOnly = useReadOnly(ws.org.org_id);
  const today = todayIn(ws.org.timezone);
  const [newBrandsOk, setNewBrandsOk] = useState(false);
  const [withDuplicates, setWithDuplicates] = useState(false);
  if (q.error) return <ErrorState error={q.error} retry={() => void q.refetch()} />;
  if (q.isPending || !q.data) return <Loading />;
  const d = q.data;
  const studio = !!ws.isCreator;
  const ctx = koopContext(d, { today, orgCurrency: ws.org.currency, canConfirmPayments: ws.can("confirm_payments"), studio });
  return (
    <Frame<KoopImport>
      kind="kooperationen" ws={ws} readOnly={!!readOnly.data}
      vorlage={studio ? STUDIO_KOOP_VORLAGE : undefined}
      parse={(rows) => {
        const r = koopRows(rows, ctx);
        return { map: r.map, rows: r.rows, newBrands: r.newBrands };
      }}
      blocked={(p) => (p.newBrands.length > 0 && !newBrandsOk ? t("Bitte bestätigen, dass die fehlenden Marken neu angelegt werden.", "Please confirm that the missing brands will be created.") : null)}
      allowDuplicates={withDuplicates}
      extra={(p) => (
        <>
          {p.newBrands.length > 0 && (
            <label className="row tk-check">
              <input type="checkbox" checked={newBrandsOk} onChange={(e) => setNewBrandsOk(e.target.checked)} />
              <span>{p.newBrands.length === 1 ? t(`Marke „${p.newBrands[0]}“ gibt es noch nicht; beim Übernehmen neu anlegen`, `Brand “${p.newBrands[0]}” does not exist yet; create it when taking over`) : t(`${p.newBrands.length} Marken gibt es noch nicht (${p.newBrands.join(", ")}); beim Übernehmen neu anlegen`, `${p.newBrands.length} brands do not exist yet (${p.newBrands.join(", ")}); create them when taking over`)}</span>
            </label>
          )}
          {p.dupCount > 0 && (
            <label className="row tk-check">
              <input type="checkbox" checked={withDuplicates} onChange={(e) => setWithDuplicates(e.target.checked)} />
              <span>{t("Dubletten trotzdem übernehmen (sonst werden sie ausgelassen)", "Take over duplicates anyway (otherwise they are skipped)")}</span>
            </label>
          )}
        </>
      )}
      run={async (rows) => {
        const brandIds = new Map<string, string>();
        const out: Result[] = [];
        for (const r of rows) {
          try {
            const x = await importKooperation(api, ws.org.org_id, r.values!, brandIds);
            out.push({ line: r.line, label: r.label, ok: true, text: x.months ? t(`übernommen, ${x.months === 1 ? t("1 Monat", "1 month") : t(`${x.months} Monate`, `${x.months} months`)} mit Stand`, `imported, ${x.months === 1 ? t("1 Monat", "1 month") : t(`${x.months} Monate`, `${x.months} months`)} with status`) : t("übernommen", "imported"), to: `/agentur/kooperationen/${x.dealId}` });
          } catch (e) {
            out.push({ line: r.line, label: r.label, ok: false, text: rowError(e) });
          }
        }
        return out;
      }}
      summary={(r) => koopSummary(r)}
      help={studio ? (
        <>
          <p>{t("Die erste Zeile enthält die Spaltennamen. Erkannt werden Marke, Titel, Modell (z. B. Einmalig, Monatlich, Raten), Stand (z. B. In Produktion, Abgeschlossen), Währung, Honorar, Beginn (Monat), Monate, Leistungen, Plattformen, Nutzungsrechte bis, Content-Deadline und Stand vergangener Monate; andere Spalten bleiben unbeachtet.",
            "The first row contains the column names. Recognised are brand, title, model (e.g. one-off, monthly, instalments), status (e.g. In production, Completed), currency, fee, start (month), months, services, platforms, usage rights until, content deadline and status of past months; other columns are ignored.")}</p>
          <ul>
            <li>{t("Die Spalte Creator kannst du weglassen; jeder Deal gehört zu deinem Profil. Spalten, die es im Creator-Konto nicht gibt, nennt die Vorschau mit „wird im Creator-Konto ignoriert“.", "You can leave out the creator column; every deal belongs to your profile. Columns that do not exist in a creator account are marked “is ignored in a creator account” in the preview.")}</li>
            <li>{t("Honorar netto: bei Monatlich der Betrag je Monat, sonst der Gesamtbetrag (bei mehreren Monaten gleichmäßig verteilt). Zahlen wie 1.234,56 oder 1234.56.", "Net fee: for monthly, the amount per month, otherwise the total amount (spread evenly over several months). Numbers like 1.234,56 or 1234.56.")}</li>
            <li>{t("Fehlende Marken legt der Import nach Rückfrage an.", "The import creates missing brands after asking.")}</li>
            <li>{t("Stand vergangener Monate: ", "Status of past months: ")}<strong>{t("abgerechnet", "invoiced")}</strong> {t("heißt, die Rechnungen sind im bisherigen System gestellt, aber noch nicht bezahlt; offene Beträge danach als ", "means the invoices were issued in the previous system but have not been paid yet; then take over open amounts as ")}<Link to="/agentur/rechnungen/import">{t("offene Rechnungen übernehmen", "open invoices")}</Link>. <strong>{t("bezahlt", "paid")}</strong> {t("heißt vollständig erledigt. Genauer geht es mit den Spalten „Abgerechnet bis“ und „Bezahlt bis“ (Monat).", "means fully settled. You can be more precise with the columns “Abgerechnet bis” and “Bezahlt bis” (month).")}</li>
            <li>{t("Es entstehen keine Rechnungsanforderungen, Glocken oder Mails. Verträge und Content kommen danach an der Kooperation dazu.", "No invoice requests, notifications or emails are created. Contracts and content are added to the deal afterwards.")}</li>
          </ul>
        </>
      ) : (
        <>
          <p>
            {t("Die erste Zeile enthält die Spaltennamen. Erkannt werden Creator (E-Mail oder Name), Marke, Titel, Modell (", "The first row contains the column names. Recognised are creator (email or name), brand, title, model (")}{d.models.length ? t("nur die freigeschalteten", "only the enabled ones") : t("alle Modelle", "all models")}{t(": Einmalig, Monatlich, Raten, Reine Provision …), Stand (z. B. In Produktion, Abgeschlossen), Währung, Honorar, Provision in Prozent, Beginn (Monat), Monate, Leistungen, Plattformen, Nutzungsrechte bis, Abrechnungsweg, Content-Deadline und Stand vergangener Monate; andere Spalten bleiben unbeachtet.", ": one-off, monthly, instalments, commission only …), status (e.g. In production, Completed), currency, fee, commission in percent, start (month), months, services, platforms, usage rights until, invoicing route, content deadline and status of past months; other columns are ignored.")}</p>
          <ul>
            <li>{t("Honorar netto: bei Monatlich der Betrag je Monat, sonst der Gesamtbetrag (bei mehreren Monaten gleichmäßig verteilt). Zahlen wie 1.234,56 oder 1234.56.", "Net fee: for monthly, the amount per month, otherwise the total amount (spread evenly over several months). Numbers like 1.234,56 or 1234.56.")}</li>
            <li>{t("Leere Provision heißt Standard des Creators bzw. der Agentur; der Satz wird wie beim Anlegen festgeschrieben. Die Agenturgebühr folgt den Konditionen.", "An empty commission means the creator's or agency's default; the rate is fixed as when creating. The agency fee follows the terms.")}</li>
            <li>{t("Creator müssen schon angelegt sein. Fehlende Marken legt der Import nach Rückfrage an.", "Creators must already exist. The import creates missing brands after asking.")}</li>
            <li>
              {t("Stand vergangener Monate: ", "Status of past months: ")}<strong>{t("abgerechnet", "invoiced")}</strong> {t("heißt, die Rechnungen sind im bisherigen System gestellt, aber noch nicht alles bezahlt; offene Beträge danach als ", "means the invoices were issued in the previous system but not everything has been paid yet; then take over open amounts as ")}<Link to="/agentur/rechnungen/import">{t("offene Rechnungen übernehmen", "open invoices")}</Link>. <strong>{t("Marke bezahlt", "Marke bezahlt (brand paid)")}</strong> {t("heißt, das Geld der Marke ist da, der Creator ist noch nicht ausgezahlt; seine offene Rechnung danach als offene Rechnung übernehmen, sie steht dann ohne Sperre im Auszahlungslauf. ", "means the brand's money has arrived but the creator has not been paid out yet; then take over the creator's open invoice, which then appears in the payout run without a block. ")}<strong>{t("bezahlt", "paid")}</strong> {t("heißt vollständig erledigt (Marke hat gezahlt, Creator ist ausgezahlt). Genauer geht es mit den Spalten „Abgerechnet bis“, „Marke bezahlt bis“ und „Bezahlt bis“ (Monat).", "means fully settled (brand has paid, creator has been paid out). You can be more precise with the columns “Abgerechnet bis”, “Marke bezahlt bis” and “Bezahlt bis” (month).")}{!ws.can("confirm_payments") && t(" Monate als bezahlt übernimmt nur, wer Zahlungen bestätigen darf.", " Only someone who may confirm payments can take over months as paid.")}
            </li>
            <li>{t("Es entstehen keine Rechnungsanforderungen, Glocken oder Mails. Verträge und Content kommen danach an der Kooperation dazu.", "No invoice requests, notifications or emails are created. Contracts and content are added to the deal afterwards.")}</li>
          </ul>
        </>
      )}
    />
  );
}

function koopSummary(r: ImportRow<KoopImport>): string {
  if (!r.values) return "–";
  const v = r.values;
  const deal = v.deal as { model?: string; start_month?: string; budget_cents?: number | null; currency?: string };
  const months = v.months.length;
  // Betrag über lib/format (Sprache und Land der Agentur, de-CH mit '), nicht fest de-DE (Portal einheitlich)
  return [deal.start_month ? `${t("ab", "from")} ${monthLabel(deal.start_month.slice(0, 7))}` : null, months > 1 ? t(`${months} Monate`, `${months} months`) : null,
    deal.budget_cents != null ? money(deal.budget_cents, deal.currency ?? "EUR") : null,
    v.newBrand ? t("neue Marke", "new brand") : null].filter(Boolean).join(" · ");
}

// ── Offene Rechnungen ─────────────────────────────────────────────────────────

function RechnungImportSeite({ ws }: { ws: StaffWorkspace }) {
  const orgId = ws.org.org_id;
  const q = { talents: useTalents(orgId), brands: useBrands(orgId), deals: useDeals(orgId), periods: usePeriods(orgId), invoices: useInvoices(orgId), cfg: useOrgConfig(orgId), settings: useOrgSettings(orgId) };
  const readOnly = useReadOnly(orgId);
  const today = todayIn(ws.org.timezone);
  const list = Object.values(q);
  const error = list.find((x) => x.error)?.error;
  if (error) return <ErrorState error={error} retry={() => list.forEach((x) => x.error && void x.refetch())} />;
  if (list.some((x) => x.isPending) || !q.cfg.data) return <Loading />;
  const policy: DunningPolicy = q.cfg.data.dunning;
  const ctx = rechnungContext({
    talents: q.talents.data ?? [], brands: q.brands.data ?? [], deals: q.deals.data ?? [], periods: q.periods.data ?? [], invoices: q.invoices.data ?? [],
    settings: (q.settings.data ?? null) as Record<string, unknown> | null, dunning: policy,
  }, { today, orgCurrency: ws.org.currency, orgCountry: ws.org.country, canConfirmPayments: ws.can("confirm_payments"), canDun: ws.can("dun"), studio: !!ws.isCreator });
  const canImport = ws.can("approve_invoices");
  const studio = !!ws.isCreator;
  // Mahnstufe, Steuerfall, PDF und Nummern gelten für beide Konten
  const hilfeGemeinsam = (
    <>
            <li>
              {t("Mahnstufe: ", "Dunning level: ")}{policy.stages.length ? policy.stages.map((st, i) => t(`${i + 1} = ${dunningStageLabel(policy, st.key, currentLang())} (${st.deadline.days} ${st.deadline.unit === "working" ? t("Werktage", "Working days") : t("Tage", "days")} Frist)`, `${i + 1} = ${dunningStageLabel(policy, st.key, currentLang())} (${st.deadline.days} ${st.deadline.unit === "working" ? t("Werktage", "Working days") : t("Tage", "days")} deadline)`)).join(", ") : t("Die Mahnleiter hat noch keine Stufen", "The dunning levels have no levels yet")}{t("; 0 oder leer = noch nicht gemahnt. Die Frist läuft ab dem Datum der letzten Mahnung, danach geht es mit der nächsten Stufe weiter.", "; 0 or empty = not yet dunned. The deadline runs from the date of the last dunning notice, then it continues with the next level.")}</li>
            <li>{t("Steuerfall: Regelbesteuerung, Kleinunternehmer, Reverse Charge EU, § 13b, nicht steuerbar oder manuell. Leer mit Steuerbetrag heißt Regelbesteuerung.", "Tax treatment: Regelbesteuerung, Kleinunternehmer, Reverse Charge EU, § 13b, nicht steuerbar or manuell. Empty with a tax amount means Regelbesteuerung.")}</li>
            <li>{t("Es gehen keine Mails raus und niemand bekommt eine Glocke. Das Original-PDF lädst du danach an der Rechnung hoch; CreatorOffice erzeugt für übernommene Rechnungen kein eigenes PDF.", "No emails go out and nobody gets a notification. You then upload the original PDF to the invoice; CreatorOffice does not create its own PDF for imported invoices.")}</li>
            <li>
              {t("Die Nummern bleiben, wie sie sind. Nummern im eigenen Muster überspringt der Nummernkreis;", "The numbers stay as they are. The number range skips numbers in your own pattern;")}{ws.isChef ? <> {t("die nächste eigene Nummer setzt du unter ", "you set the next own number under ")}<Link to="/agentur/einstellungen/nummernkreise">{t("Einstellungen · Nummernkreise", "Settings · Number ranges")}</Link>.</> : t(" die nächste eigene Nummer setzt der Admin.", " the admin sets the next own number.")}
              {" "}{t("Im ", "In the ")}{bookkeepingExportName(ws.org.country)} {t("sind übernommene Rechnungen abwählbar, weil sie im bisherigen System schon gebucht sind.", "imported invoices can be deselected because they are already posted in the previous system.")}</li>
    </>
  );
  return (
    <Frame<RechnungImport>
      kind="rechnungen" ws={ws} readOnly={!!readOnly.data}
      vorlage={studio ? STUDIO_RECHNUNG_VORLAGE : undefined}
      noRight={canImport ? null : t("Offene Rechnungen übernimmt, wer Rechnungen freigeben darf. Wende dich an deinen Admin.", "Only someone who may approve invoices can take over open invoices. Contact your admin.")}
      top={
        <EinzelRechnungErfassen orgId={orgId} ctx={ctx} policy={policy} disabled={!!readOnly.data || !canImport} currency={ws.org.currency} nurAnMarke={studio}
          brands={(q.brands.data ?? []).map((b) => ({ id: b.id, name: b.name }))}
          talents={(q.talents.data ?? []).filter((t) => t.status !== "archived").map((t) => ({ id: t.id, label: talentFullName(t as never), key: (t.email ?? "").trim() || talentFullName(t as never) }))} />
      }
      parse={(rows) => {
        const r = rechnungRows(rows, ctx);
        return { ...r, rows: studio ? nurAnMarke(r.rows) : r.rows, newBrands: [] };
      }}
      blocked={() => null}
      allowDuplicates={false}
      extra={() => null}
      run={async (rows) => {
        const out: Result[] = [];
        for (const r of rows) {
          try {
            const x = await importRechnung(api, orgId, r.values!);
            out.push({ line: r.line, label: r.label, ok: true, text: x.status === "paid" ? t("übernommen, bezahlt", "imported, paid") : x.status === "approved" ? t("übernommen, im Auszahlungslauf", "imported, in payout run") : t("übernommen, versendet", "imported, sent"), to: `/agentur/rechnungen/${x.invoiceId}` });
          } catch (e) {
            out.push({ line: r.line, label: r.label, ok: false, text: rowError(e) });
          }
        }
        return out;
      }}
      summary={(r) => rechnungSummary(r)}
      help={studio ? (
        <>
          <p>{t("Rechnungen an Marken, die vor dem Umstieg im bisherigen System gestellt wurden und noch offen sind. Erkannt werden Nummer (wie im bisherigen System), Art („an Marke“), Marke, Kooperation (Titel), Leistungsmonat, Rechnungsdatum, Fälligkeit (Datum oder Tage), Netto, Steuer, Brutto (zwei von drei genügen), Währung, Steuerfall, Bereits bezahlt, Bezahlt am, Mahnstufe, Letzte Mahnung und Bemerkung.",
            "Invoices to brands issued in the previous system before switching that are still open. Recognised are number (as in the previous system), type (“an Marke”), brand, deal (title), service month, invoice date, due date (date or days), net, tax, gross (two of three are enough), currency, tax treatment, already paid, paid on, dunning level, last dunning notice and remark.")}</p>
          <ul>
            <li>{t("Sie stehen danach als versendet in der Liste, zählen in der Übersicht und laufen ins Mahnwesen.", "They then appear as sent in the list, count in the overview and go into dunning.")}</li>
            {hilfeGemeinsam}
          </ul>
        </>
      ) : (
        <>
          <p>
            {t("Rechnungen, die vor dem Umstieg im bisherigen System gestellt wurden und noch offen sind. Erkannt werden Nummer (wie im bisherigen System), Art (an Marke, Creator an Agentur, Provisionsrechnung an Creator oder Creator an Marke), Marke, Creator, Kooperation (Titel), Leistungsmonat, Rechnungsdatum, Fälligkeit (Datum oder Tage), Netto, Steuer, Brutto (zwei von drei genügen), Währung, Steuerfall, Bereits bezahlt, Bezahlt am, Mahnstufe, Letzte Mahnung und Bemerkung.", "Invoices issued in the previous system before switching that are still open. Recognised are number (as in the previous system), type (to brand, creator to agency, commission invoice to creator or creator to brand), brand, creator, deal (title), service month, invoice date, due date (date or days), net, tax, gross (two of three are enough), currency, tax treatment, already paid, paid on, dunning level, last dunning notice and remark.")}</p>
          <ul>
            <li>{t("Rechnungen an die Marke stehen danach als versendet in der Liste, zählen in Übersicht und Kennzahlen und laufen ins Mahnwesen.", "Invoices to the brand then appear as sent in the list, count in overview and metrics and go into dunning.")}</li>
            <li>{t("Rechnungen eines Creators an die Agentur stehen freigegeben im Auszahlungslauf (noch auszuzahlen); ein schon ausgezahlter Teil steht unter „Bereits bezahlt“. Zahlt die Agentur erst nach Zahlungseingang der Marke aus, wird die Rechnung frei, sobald die Zahlung der Marke im Portal steht: den Monat der Kooperation als „Marke bezahlt“ übernehmen oder die bezahlte Rechnung an die Marke mit „Bereits bezahlt“ = ja mit übernehmen.", "A creator's invoices to the agency appear approved in the payout run (still to be paid out); a part already paid out appears under “Bereits bezahlt”. If the agency only pays out after the brand has paid, the invoice is released as soon as the brand's payment is recorded in the portal: take over the deal month as “Marke bezahlt” or also take over the paid invoice to the brand with “Bereits bezahlt” = yes.")}</li>
            <li>{t("Direkter Weg: Provisionsrechnungen der Agentur an Creator stehen als versendet in der Liste und laufen ins Mahnwesen (Zahlungserinnerung an den Creator); Rechnungen eines Creators an die Marke stehen als versendet zum Überwachen und lassen sich im Auftrag des Creators mahnen.", "Direct route: the agency's commission invoices to creators appear as sent in the list and go into dunning (payment reminder to the creator); a creator's invoices to the brand appear as sent for monitoring and can be dunned on the creator's behalf.")}</li>
            {hilfeGemeinsam}
          </ul>
        </>
      )}
    />
  );
}

function rechnungSummary(r: ImportRow<RechnungImport>): ReactNode {
  if (!r.values) return "–";
  const v = r.values;
  return (
    <>
      {LEGACY_KIND_LABEL[v.kind]} · {date(v.issue_date)} · <Money cents={v.gross_cents} currency={v.currency} />
      {v.paid_cents > 0 && <> {t("· bezahlt ", "· paid ")}<Money cents={v.paid_cents} currency={v.currency} /></>}
      {v.due_date ? t(` · fällig ${date(v.due_date)}`, ` · due ${date(v.due_date)}`) : ""}
    </>
  );
}

// ── Gemeinsamer Rahmen ─────────────────────────────────────────────────────────

interface Parsed<P> { map: Record<string, number>; rows: ImportRow<P>[]; newBrands: string[] }

function Frame<P>({ kind, ws, readOnly, noRight = null, parse, blocked, allowDuplicates, extra, run, summary, help, top = null, vorlage }: {
  kind: BestandKind;
  /** Inhalt der Vorlage (Studio: ohne Provision und Creator-Rechnungsarten); sonst die Vorlage der Agentur */
  vorlage?: string;
  /** Zusätzlicher Abschnitt über der Tabelle (Einzelne Rechnung erfassen, PARITAET-10) */
  top?: ReactNode;
  ws: StaffWorkspace;
  readOnly: boolean;
  noRight?: string | null;
  parse: (rows: string[][]) => Parsed<P>;
  blocked: (p: Parsed<P> & { dupCount: number }) => string | null;
  allowDuplicates: boolean;
  extra: (p: Parsed<P> & { dupCount: number }) => ReactNode;
  run: (rows: ImportRow<P>[]) => Promise<Result[]>;
  summary: (r: ImportRow<P>) => ReactNode;
  help: ReactNode;
}) {
  const l = LABEL[kind];
  const { busy, run: act } = useAction();
  const [text, setText] = useState("");
  const [table, setTable] = useState<string[][] | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [results, setResults] = useState<Result[] | null>(null);

  // Ohne Zwischenspeicher: die Prüfung hängt an den geladenen Daten der Seite und ist für 500 Zeilen schnell genug
  const parsed = (() => {
    if (!table && !text.trim()) return null;
    const rows = table ?? parseCsv(text);
    const header = rows[0] ?? [];
    const p = parse(rows);
    const unknown = header.filter((_, i) => !Object.values(p.map).includes(i)).map((h) => h.trim()).filter(Boolean);
    return { ...p, header, unknown, dupCount: p.rows.filter((r) => r.values && r.duplicate).length };
  })();

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setResults(null);
    setFileError(null);
    setFileName(f.name);
    const buf = await f.arrayBuffer();
    if (looksLikeXlsx(f.name, new Uint8Array(buf.slice(0, 4)))) {
      try {
        setTable(await readXlsx(buf));
        setText("");
      } catch (err) {
        setTable(null);
        setFileError(fehlerText(err));
      }
      return;
    }
    let content: string;
    try { content = new TextDecoder("utf-8", { fatal: true }).decode(buf); } catch { content = new TextDecoder("windows-1252").decode(buf); }
    setTable(null);
    setText(content);
  }

  const rows = parsed?.rows ?? [];
  const tooMany = rows.length > MAX_ROWS;
  const ready = rows.filter((r) => r.values && (allowDuplicates || !r.duplicate));
  const faulty = rows.filter((r) => !r.values).length;
  const dupCount = parsed?.dupCount ?? 0;
  const block = parsed ? blocked(parsed) : null;
  const disabled = !!busy || !ready.length || tooMany || !!block || readOnly || !!noRight;

  async function start() {
    const out = await act("import", () => run(ready.slice(0, MAX_ROWS)));
    if (!out) return;
    setResults(out);
    setText("");
    setTable(null);
    setFileName(null);
  }
  const okCount = results?.filter((r) => r.ok).length ?? 0;

  return (
    <main id="main" className="content tk-sd">
      <div className="pagehead">
        <div>
          <div className="eyebrow">{t("Bestand übernehmen", "Take over existing data")}</div>
          <h1>{l.title}</h1>
          <p>{t("Aus dem bisherigen System, aus Excel, Numbers oder Google Tabellen. Erst prüfen, dann übernehmen.", "From the previous system, from Excel, Numbers or Google Sheets. Check first, then take over.")}</p>
        </div>
        <div className="row">
          <Link className="btn" to={l.back}>{l.backLabel}</Link>
        </div>
      </div>
      <DemoNote />
      {readOnly && (
        <div className="notice warning" role="status">{t("Diese Agentur nutzt CreatorOffice derzeit nur lesend (Testphase abgelaufen, gesperrt oder gekündigt). Eine Übernahme ist erst nach der Freischaltung möglich.", "This agency currently uses CreatorOffice read-only (trial expired, suspended or terminated). A takeover is only possible after reactivation.")}</div>
      )}
      {noRight && <div className="notice" role="status">{noRight}</div>}
      {top}

      {results && (
        <div className="notice stack" role="status">
          <p><strong>{okCount === 1 ? `1 ${l.one} ${l.verb}` : `${okCount} ${l.many} ${l.verb}`}{results.length > okCount ? t(`, ${results.length - okCount} nicht`, `, ${results.length - okCount} not`) : ""}.</strong></p>
          <ul>
            {results.map((r) => (
              <li key={r.line}>{t("Zeile ", "Row ")}{r.line} · {r.to && r.ok ? <Link to={r.to}>{r.label}</Link> : r.label}: {r.text}</li>
            ))}
          </ul>
          <div className="row">
            <Link className="btn" to={l.back}>{t("Zur Liste", "Go to list")}</Link>
            <button type="button" onClick={() => setResults(null)}>{t("Hinweis schließen", "Close notice")}</button>
          </div>
        </div>
      )}

      <section className="panel stack">
        <h2>{t("1. Tabelle vorbereiten", "1. Prepare the table")}</h2>
        {help}
        <div className="row">
          <button type="button" onClick={() => downloadBlob(l.template, new Blob(["﻿" + (vorlage ?? (kind === "kooperationen" ? KOOP_TEMPLATE : RECHNUNG_TEMPLATE))], { type: "text/csv;charset=utf-8" }))}>
            {t("Vorlage herunterladen", "Download template")}</button>
        </div>
      </section>

      <section className="panel stack">
        <h2>{t("2. Datei wählen oder Zellen einfügen", "2. Choose a file or paste cells")}</h2>
        <label>
          {t("Excel-Datei (.xlsx, erstes Tabellenblatt) oder CSV", "Excel file (.xlsx, first sheet) or CSV")}<input type="file" accept=".xlsx,.csv,.txt,text/csv,text/plain,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={(e) => void onFile(e)} />
        </label>
        {fileName && !fileError && <small>{t("Gelesen: ", "Read: ")}{fileName}{table ? t(` · ${Math.max(0, table.length - 1)} Zeilen`, ` · ${Math.max(0, table.length - 1)} rows`) : ""}</small>}
        {fileError && <small className="tk-error" role="alert">{fileError}</small>}
        <label>
          {t("Oder Zellen hier einfügen (mit Kopfzeile)", "Or paste cells here (with header row)")}<textarea rows={5} value={text} onChange={(e) => { setText(e.target.value); setTable(null); setFileName(null); setResults(null); }} spellCheck={false} />
        </label>
      </section>

      {parsed && (
        <section className="panel stack" aria-live="polite">
          <h2>{t("3. Prüfen und übernehmen", "3. Check and take over")}</h2>
          <p>
            {t("Erkannte Spalten: ", "Recognised columns: ")}{Object.keys(parsed.map).map((k) => BESTAND_COLUMN_LABEL[k] ?? k).join(", ") || t("keine", "none")}.
            {parsed.unknown.length > 0 && t(` Nicht erkannt und ausgelassen: ${parsed.unknown.join(", ")}.`, ` Not recognised and skipped: ${parsed.unknown.join(", ")}.`)}
          </p>
          {tooMany && <p className="tk-error" role="alert">{t("Die Tabelle hat ", "The table has ")}{rows.length} {t("Zeilen. Bitte in Teilen zu höchstens ", "rows. Please take over in parts of at most ")}{MAX_ROWS} {t("Zeilen übernehmen.", "rows.")}</p>}
          <p>
            {rows.length === 1 ? t("1 Zeile", "1 row") : t(`${rows.length} Zeilen`, `${rows.length} rows`)}: {ready.length} {t("bereit", "ready")}{faulty ? t(`, ${faulty} mit Fehlern (werden ausgelassen)`, `, ${faulty} with errors (will be skipped)`) : ""}
            {dupCount ? `, ${dupCount} ${dupCount === 1 ? t("sieht aus wie eine Dublette", "looks like a duplicate") : t("sehen aus wie Dubletten", "look like duplicates")}` : ""}.
          </p>
          {extra(parsed)}
          {block && <p className="tk-error" role="alert">{block}</p>}
          <div className="tablewrap">
            <table>
              <caption className="sr-only">{t("Vorschau", "Preview")}</caption>
              <thead>
                <tr>
                  <th scope="col">{t("Zeile", "Row")}</th>
                  <th scope="col">{l.one}</th>
                  <th scope="col">{t("Angaben", "Details")}</th>
                  <th scope="col" className="tk-links">{t("Prüfung", "Check")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, MAX_ROWS).map((r) => (
                  <tr key={r.line}>
                    <td className="num">{r.line}</td>
                    <td><strong>{r.label}</strong></td>
                    <td className="tk-umbruch tk-links"><small>{summary(r)}</small></td>
                    <td className="tk-umbruch tk-links">
                      <PillView pill={!r.values ? pill(t("Fehler", "Error"), "error") : r.duplicate ? pill(allowDuplicates ? t("Dublette, wird übernommen", "Duplicate, will be taken over") : t("Dublette, ausgelassen", "Duplicate, skipped"), "warning") : pill(t("Bereit", "Ready"), "success")} />
                      {r.errors.length > 0 && <small>{r.errors.join(" · ")}</small>}
                      {r.values && r.duplicate && <small>{r.duplicate}</small>}
                      {r.values && r.notes && r.notes.length > 0 && <small>{r.notes.join(" · ")}</small>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row">
            <button type="button" className="primary" disabled={disabled} onClick={() => void start()}>
              {busy === "import" ? t("Wird übernommen …", "Taking over …") : ready.length === 1 ? t(`1 ${l.one} übernehmen`, `Take over 1 ${l.one}`) : t(`${ready.length} ${l.many} übernehmen`, `Take over ${ready.length} ${l.many}`)}
            </button>
            <button type="button" disabled={!!busy} onClick={() => { setText(""); setTable(null); setFileName(null); }}>{t("Verwerfen", "Discard")}</button>
          </div>
          {!ws.isChef && kind === "kooperationen" && <small className="muted">{t("Übernommen werden nur Kooperationen von Creatorn, die du betreust.", "Only deals of creators you manage are taken over.")}</small>}
        </section>
      )}
    </main>
  );
}
